import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ProductService from '../../../components/ProductService';
import { ProductComponentLink, ProductSelectionItem } from '../types';
import { parsePrice } from '../utils/formatters';

export const resolveProductImageUrl = (rawUrl?: string | null): string | undefined => {
  if (!rawUrl) {
    return undefined;
  }

  if (/^https?:\/\//i.test(rawUrl)) {
    return rawUrl;
  }

  const basePath = process.env.REACT_APP_DATA_PIC_PATH;
  if (!basePath) {
    return rawUrl;
  }

  const trimmedBase = basePath.replace(/\/+$/, '');
  const trimmedPath = String(rawUrl).replace(/^\/+/, '');
  return `${trimmedBase}/${trimmedPath}`;
};

export interface InventoryProduct extends ProductSelectionItem {}

type RawComponentRef = {
  _id?: string;
  id?: string;
  name?: string;
  component?: string;
  product?: string;
};

type RawProductComponent = {
  product?: string | RawComponentRef | null;
  component?: string | RawComponentRef | null;
  quantity?: number | string | null;
  name?: string | null;
};

type RawProduct = {
  _id?: string;
  id?: string;
  name?: string;
  description?: string;
  barcode?: string;
  sku?: string;
  reference?: string;
  type?: string;
  url_product?: string;
  isComposant?: boolean;
  components?: RawProductComponent[] | null;
  regular_price?: number | string | null;
  discounted_price?: number | string | null;
  price?: number | string | null;
};

const normalizeProductComponents = (
  components?: RawProductComponent[] | null,
): ProductComponentLink[] | undefined => {
  if (!Array.isArray(components)) {
    return undefined;
  }

  const normalized = components
    .map((item): ProductComponentLink | null => {
      const rawReference = item?.product ?? item?.component;
      const componentId =
        typeof rawReference === 'object'
          ? rawReference?._id ?? rawReference?.id ?? rawReference?.product ?? rawReference?.component
          : rawReference;

      if (!componentId) {
        return null;
      }

      const rawQuantity = item?.quantity;
      const quantityNumber = Number(rawQuantity);

      if (!Number.isFinite(quantityNumber) || quantityNumber <= 0) {
        return null;
      }

      const name =
        item?.name ??
        (typeof rawReference === 'object' ? rawReference?.name ?? undefined : undefined);

      const entry: ProductComponentLink = {
        product: String(componentId),
        quantity: quantityNumber,
        component: String(componentId),
      };

      const resolvedName = name ?? undefined;
      if (resolvedName !== undefined) {
        entry.name = resolvedName;
      }

      return entry;
    })
    .filter((entry): entry is ProductComponentLink => entry !== null);

  return normalized.length ? normalized : undefined;
};

const normalizeProduct = (product: RawProduct): InventoryProduct | null => {
  const id = product._id ?? product.id;
  const rawName = product.name?.toString().trim();
  const rawDescription = product.description?.toString().trim();
  const label = rawName || rawDescription;
  if (!id || !label) {
    return null;
  }
  const description = rawDescription || rawName || undefined;
  const discountedPrice = parsePrice(product.discounted_price);
  const regularPrice = parsePrice(product.regular_price);
  const fallbackPrice = parsePrice(product.price);
  const price = discountedPrice ?? regularPrice ?? fallbackPrice;
  return {
    id: String(id),
    name: rawName || description || label,
    description,
    barcode: product.barcode ?? undefined,
    sku: product.sku ?? product.reference ?? undefined,
    type: product.type ?? undefined,
    imageUrl: resolveProductImageUrl(product.url_product ?? null),
    isComposant: product.isComposant ?? undefined,
    components: normalizeProductComponents(product.components ?? undefined),
    price,
    regularPrice,
    discountedPrice,
  };
};

export const useInventoryProducts = () => {
  const queryClient = useQueryClient();

  React.useEffect(() => {
    const handleProductsCacheUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<{ products?: RawProduct[] }>;
      const items = Array.isArray(customEvent.detail?.products) ? customEvent.detail.products : [];
      if (!items.length) {
        return;
      }

      const normalized = items
        .map((item) => normalizeProduct(item))
        .filter((item): item is InventoryProduct => Boolean(item));

      queryClient.setQueryData(['inventory', 'products', 'all'], normalized);
    };

    window.addEventListener(ProductService.PRODUCT_CACHE_UPDATED_EVENT, handleProductsCacheUpdated);
    return () => {
      window.removeEventListener(ProductService.PRODUCT_CACHE_UPDATED_EVENT, handleProductsCacheUpdated);
    };
  }, [queryClient]);

  return useQuery<InventoryProduct[]>({
    queryKey: ['inventory', 'products', 'all'],
    queryFn: async () => {
      const response = await ProductService.fetchProducts();
      // fetchProducts now returns an array directly from NDJSON stream
      const items = Array.isArray(response) ? response : [];
      if (!Array.isArray(items)) {
        return [];
      }
      return items
        .map((item) => normalizeProduct(item as RawProduct))
        .filter((item): item is InventoryProduct => Boolean(item));
    },
  });
};
